import React, { useState } from 'react';
import { Grid, TextField, Button, Link, Divider, Typography, Dialog, DialogTitle, DialogContent, Alert, IconButton, InputAdornment, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useTranslation } from 'react-i18next';

import { useLoginMutation, useLoginWithFirebaseMutation } from '../api/apiSlice';
import { useCompleteLogin } from '../hooks/useCompleteLogin';
import { getAuthErrorMessage } from '../utils/authError';
import SocialLoginButtons from './SocialLoginButtons';
import ClubPicker from './ClubPicker';
import TivityLogo from './TivityLogo';
import AuthLayout from './AuthLayout';
import { ClubListItem } from '../types/api';

function Login() {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [formError, setFormError] = useState('');
    const [pending, setPending] = useState(false);
    const [showSignupHint, setShowSignupHint] = useState(false);
    const [clubPickerOpen, setClubPickerOpen] = useState(false);
    const [pendingIdToken, setPendingIdToken] = useState<string | null>(null);

    const [login, { isLoading: isLoggingIn }] = useLoginMutation();
    const [loginWithFirebase, { isLoading: isFirebaseLoggingIn }] = useLoginWithFirebaseMutation();
    const completeLogin = useCompleteLogin();

    const clearMessages = () => {
        setFormError('');
        setPending(false);
        setShowSignupHint(false);
    };

    const finishLogin = (token: string) => {
        const { pending: isPending } = completeLogin(token);
        setFormError('');
        setShowSignupHint(false);
        setPending(isPending);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        clearMessages();

        try {
            const token = await login({ email, password }).unwrap();
            finishLogin(token);
        } catch (error: any) {
            setFormError(getAuthErrorMessage(error, t));
            setShowSignupHint(true);
        }
    };

    const handleSocialLogin = async (signIn: () => Promise<string>) => {
        clearMessages();
        let idToken: string;

        try {
            idToken = await signIn();
        } catch {
            setFormError(t('common.genericError'));
            return;
        }

        try {
            const token = await loginWithFirebase({ idToken }).unwrap();
            finishLogin(token);
        } catch (error: any) {
            if (error?.data?.code === 'CLUB_REQUIRED') {
                setPendingIdToken(idToken);
                setClubPickerOpen(true);
            } else {
                setFormError(getAuthErrorMessage(error, t));
            }
        }
    };

    const handleClubPicked = async (club: ClubListItem) => {
        setClubPickerOpen(false);
        if (!pendingIdToken) return;

        try {
            const token = await loginWithFirebase({ idToken: pendingIdToken, clubId: club.ID }).unwrap();
            finishLogin(token);
        } catch (error: any) {
            setFormError(getAuthErrorMessage(error, t));
        } finally {
            setPendingIdToken(null);
        }
    };

    const isSubmitting = isLoggingIn || isFirebaseLoggingIn;

    return (
        <>
            <Button
                variant="contained"
                color='button'
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    margin: '20px',
                    zIndex: 1,
                }}
                onClick={() => {
                    window.history.back();
                }}
            ><ArrowBackIcon/>
                {t('login.back')}
            </Button>
            <AuthLayout>
                <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} sx={{ textAlign: "center" }}>
                    <Link href="/" underline="none" sx={{ display: 'inline-flex', justifyContent: 'center' }}>
                        <TivityLogo />
                    </Link>
                    </Grid>
                    {formError && (
                        <Grid item xs={12}>
                            <Alert severity="error">{formError}</Alert>
                        </Grid>
                    )}
                    {pending && (
                        <Grid item xs={12}>
                            <Alert severity="info">{t('login.pendingApproval')}</Alert>
                        </Grid>
                    )}
                    {showSignupHint && (
                        <Grid item xs={12}>
                            <Typography variant="body2" align="center" color="text.secondary">
                                {t('login.noAccountPrompt')}{' '}
                                <Link href="/clubs" underline="hover">{t('login.signupCta')}</Link>
                            </Typography>
                        </Grid>
                    )}
                    <Grid item xs={12}>
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                            id="email"
                            type="email"
                            label={t('login.email')}
                            variant="outlined"
                            fullWidth
                            required
                            value={email}
                            onChange={(e) => { setEmail(e.target.value); clearMessages(); }}
                            error={formError !== ''}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                            id="password"
                            type={showPassword ? 'text' : 'password'}
                            label={t('login.password')}
                            variant="outlined"
                            fullWidth
                            required
                            value={password}
                            onChange={(e) => { setPassword(e.target.value); clearMessages(); }}
                            error={formError !== ''}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                                            onClick={() => setShowPassword((show) => !show)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Button
                            id="connect-btn"
                            variant="contained"
                            color="primary"
                            fullWidth
                            type="submit"
                            disabled={isSubmitting}
                            startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : undefined}
                            >
                            {t('login.submit')}
                            </Button>
                        </Grid>
                        <Grid item xs={12}>
                            <Link href="/clubs" color="inherit">
                                <Button
                                id="inscription-btn"
                                variant="outlined"
                                color="primary"
                                fullWidth
                                >
                                {t('login.signupCta')}
                                </Button>
                            </Link>
                        </Grid>
                        <Grid item xs={12}>
                            <Link href="#" variant="body2" style={{ color: 'black' }} underline='none'>
                                {t('login.forgotPassword')}
                            </Link>
                        </Grid>
                        <Grid item xs={12}>
                            <Divider>
                                <Typography variant="body2" color="text.secondary">
                                    {t('login.orDivider')}
                                </Typography>
                            </Divider>
                        </Grid>
                        <SocialLoginButtons onProviderSelect={handleSocialLogin} />
                        </Grid>
                    </form>
                    </Grid>
                </Grid>
            </AuthLayout>
            <Dialog open={clubPickerOpen} onClose={() => setClubPickerOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{t('clubs.chooseClubPrompt')}</DialogTitle>
                <DialogContent>
                    <ClubPicker onSelect={handleClubPicked} />
                </DialogContent>
            </Dialog>
        </>
    );
}

export default Login;
